import { Component, h, Element, State, Prop, Build } from '@stencil/core';

/**
 * @slot heading - <me-heading variant="3"> -
 *   IMPORTANT: You can pass heading only if `cards` slot contains 3 or 4 items
 * @slot cards - {required}, {min-items: 2}, {max-items: 4}, <features-card-type-1 variant=["1", "2", "3"]> -
 *   IMPORTANT: If you transfer to slot 2 items - set `variant="1"`, if 3 - set `variant="2"`, if 4 - set `variant="3"`
 */

@Component({
  tag: 'features-type-11',
  styleUrl: 'index.scss',
  shadow: false,
})
export class FeaturesType11 {
  @Element() el: HTMLElement;
  @State() cardsCount: number = 0;
  @State() hasHeading: boolean = false;
  /**
   * Content alignment: 'left' (default) or 'center'.
   */
  @Prop() contentAlign: 'left' | 'center' = 'left';

  /**
   * Background color theme. Use 'white' for light backgrounds, 'dark' for dark backgrounds, 'signnow' for SignNow brand colors background.
   * @type {string}
   * @default 'white'
   * {enum}: ['white', 'dark', 'signnow']
   */
  @Prop() theme: 'white' | 'dark' | 'signnow' = 'white';

  /**
   * Run slot-counting queries BEFORE the first client render.
   * During hydration the host element is already in the DOM, so querySelectorAll
   * and children traversal work correctly. Doing this in componentWillLoad
   * (instead of componentDidLoad) means Stencil includes the values in render #1
   * and we avoid an expensive extra re-render.
   */
  componentWillLoad() {
    if (!Build.isBrowser) {
      return;
    }

    this.updateCardsCount();
    this.updateHasHeading();
  }

  updateCardsCount() {
    const cards = this.el.querySelectorAll('[slot="cards"]');
    this.cardsCount = cards.length;
  }

  updateHasHeading() {
    const headingNodes = Array.from(this.el.children).filter(node => node.getAttribute && node.getAttribute('slot') === 'heading');
    this.hasHeading = headingNodes.length > 0;
  }

  render() {
    let cardsModifier = '';
    if (this.cardsCount === 2) cardsModifier = ' features-type-11__cards--dual';
    if (this.cardsCount === 3) cardsModifier = ' features-type-11__cards--triple';
    if (this.cardsCount === 4) cardsModifier = ' features-type-11__cards--quad';

    const sectionClass = `features-type-11${this.contentAlign === 'center' ? ' features-type-11--centered' : ''} features-type-11--${this.theme}`;

    return (
      <me-section class={sectionClass} yGap="2xl" yGapLg="5xl">
        <me-row>
          <me-column spanLg="12" class={this.hasHeading ? '' : 'me-column--hidden'}>
            <slot name="heading"></slot>
          </me-column>
          <me-column spanLg="12" class={`features-type-11__cards${cardsModifier}`}>
            <slot name="cards"></slot>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
